import { useMemo, useState } from 'react';
import { Copy, Flame, Sparkles, X } from 'lucide-react';
import { PlayerFlagChip } from '@/components/PlayerFlagChip';
import { PlayerLabel } from '@/components/PlayerLabel';
import { ScoreBadge } from '@/components/ScoreBadge';
import type { AutofillPlan, PickReason, PlannedSkip } from '@/lib/autofillPlan';
import type { Candidate, EventType, Participation, SlotKey } from '@/lib/api';
import { buildSlotDmText, DM_TEXT_MAX } from '@/lib/slotDm';

const ACTION_LABEL: Record<AutofillPlan['action'], string> = {
  even: 'Distribute across both slots',
  strong: 'Strong-Slot autofill',
  weak: 'Weak-Slot autofill',
  strong_a_weak_b: 'Strong A, Weak B',
  weak_a_strong_b: 'Weak A, Strong B',
};

function reasonText(r: PickReason): string {
  switch (r.kind) {
    case 'forced':
      return r.forced === 'r5'
        ? 'R5 — forced'
        : r.forced === 'r4'
          ? 'R4 — forced'
          : 'Never-Sub — forced';
    case 'soft':
      return 'Green flag — soft force';
    case 'strong':
      return `Ranked ${r.rank}/${r.total} by score`;
    case 'weak':
      return `Ranked ${r.rank}/${r.total} (weakest first)`;
  }
}

function skipText(s: PlannedSkip): string {
  switch (s.reason.kind) {
    case 'red_carded':
      return 'Red-carded — excluded';
    case 'capacity_full':
      return `Slot ${s.reason.slot} full — no room left`;
    case 'no_open_slot':
      return 'Neither slot they applied for had room';
  }
}

const ROLE_BADGE: Record<string, string> = {
  starter: 'bg-emerald-700/50 text-emerald-100',
  sub: 'bg-sky-700/50 text-sky-100',
};

function CandidateMarkers({ candidate }: { candidate: Candidate }) {
  const notPicked = candidate.metrics?.consecutiveNotPicked ?? 0;
  const noShows = candidate.metrics?.consecutiveNoShows ?? 0;
  const leave = candidate.player.currentLeave;
  const leaveRange = leave
    ? leave.endsAt
      ? `${leave.startsAt} – ${leave.endsAt}`
      : `from ${leave.startsAt}`
    : null;
  const leaveTitle = leave
    ? leave.reason
      ? `On leave (${leaveRange}) — ${leave.reason}`
      : `On leave (${leaveRange})`
    : null;
  return (
    <>
      <PlayerFlagChip
        playerId={candidate.player.id}
        flags={candidate.player.activeFlags ?? []}
      />
      {leave && (
        <span
          title={leaveTitle ?? undefined}
          className="inline-flex items-center rounded bg-teal-900/50 px-1 py-0.5 text-[10px] font-semibold text-teal-200"
        >
          🌴
        </span>
      )}
      {notPicked >= 1 && (
        <span
          title={`Not picked ${notPicked}× in a row — prioritise`}
          className="inline-flex items-center gap-0.5 rounded bg-orange-900/50 px-1 py-0.5 text-[10px] font-semibold text-orange-300"
        >
          <Flame size={11} />
          {notPicked}
        </span>
      )}
      {noShows >= 1 && (
        <span
          title={`${noShows} no-shows in a row`}
          className="inline-flex items-center gap-0.5 rounded bg-red-900/50 px-1 py-0.5 text-[10px] font-semibold text-red-300"
        >
          🚫{noShows}
        </span>
      )}
    </>
  );
}

export function AutofillPreviewModal({
  plan,
  onConfirm,
  onCancel,
  isPending,
  eventType,
  slotTimes,
  participations,
}: {
  plan: AutofillPlan;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
  eventType: EventType;
  slotTimes: Record<SlotKey, string | undefined>;
  participations: Participation[];
}) {
  const bySlot = new Map<SlotKey, typeof plan.picks>();
  for (const p of plan.picks) {
    const list = bySlot.get(p.slot) ?? [];
    list.push(p);
    bySlot.set(p.slot, list);
  }
  const slots: SlotKey[] =
    plan.slot ? [plan.slot] : (['A', 'B'] as SlotKey[]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onCancel}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded border border-slate-800 bg-slate-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-300" />
            <h2 className="text-sm font-semibold text-slate-100">
              Autofill preview — {ACTION_LABEL[plan.action]}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-4 py-3">
          {plan.picks.length === 0 && (
            <p className="text-sm text-slate-400">
              No candidates would be picked. Nothing to apply.
            </p>
          )}

          {slots.map((slot) => {
            const rows = bySlot.get(slot) ?? [];
            if (rows.length === 0 && plan.slot) return null;
            return (
              <div key={slot} className="mb-4">
                <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">
                  <span>
                    Slot {slot}{' '}
                    <span className="text-slate-500">
                      ({rows.length} pick{rows.length === 1 ? '' : 's'})
                    </span>
                  </span>
                  <span className="flex-1" />
                  <PlanCopyDmButton
                    eventType={eventType}
                    slot={slot}
                    startsAtServer={slotTimes[slot]}
                    plan={plan}
                    participations={participations}
                  />
                </div>
                {rows.length === 0 ? (
                  <p className="text-xs text-slate-600">No picks in this slot.</p>
                ) : (
                  <ul className="space-y-1">
                    {rows.map((p, i) => (
                      <li
                        key={p.candidate.player.id}
                        className="flex items-center gap-2 rounded bg-slate-900/60 px-2 py-1 text-sm"
                      >
                        <span className="w-6 text-xs tabular-nums text-slate-500">
                          {i + 1}
                        </span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                            ROLE_BADGE[p.role] ?? 'bg-slate-700 text-slate-200'
                          }`}
                        >
                          {p.role}
                          <span className="ml-0.5 opacity-60">#{p.position}</span>
                        </span>
                        <PlayerLabel
                          player={p.candidate.player}
                          snapshot={p.candidate.latestSnapshot}
                          className="min-w-0"
                        />
                        <CandidateMarkers candidate={p.candidate} />
                        <span className="flex-1" />
                        <span className="hidden text-[11px] text-slate-500 sm:inline">
                          {reasonText(p.reason)}
                        </span>
                        <ScoreBadge breakdown={p.breakdown} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}

          {plan.skipped.length > 0 && (
            <div className="mt-3 border-t border-slate-800 pt-3">
              <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
                Skipped ({plan.skipped.length})
              </div>
              <ul className="space-y-1">
                {plan.skipped.map((s) => (
                  <li
                    key={s.candidate.player.id}
                    className="flex items-center gap-2 rounded bg-slate-900/30 px-2 py-1 text-xs text-slate-400"
                  >
                    <PlayerLabel
                      player={s.candidate.player}
                      snapshot={s.candidate.latestSnapshot}
                      className="min-w-0"
                    />
                    <CandidateMarkers candidate={s.candidate} />
                    <span className="flex-1" />
                    <span className="text-[11px] text-slate-500">{skipText(s)}</span>
                    <ScoreBadge breakdown={s.breakdown} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-800 bg-slate-950/80 px-4 py-3">
          <span className="text-xs text-slate-500">
            {plan.picks.length} pick{plan.picks.length === 1 ? '' : 's'} · {plan.skipped.length}{' '}
            skipped
          </span>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              disabled={isPending}
              className="rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isPending || plan.picks.length === 0}
              className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-3 py-1.5 text-xs font-semibold text-emerald-100 hover:bg-emerald-800/60 disabled:opacity-50"
            >
              <Sparkles size={12} />
              {isPending ? 'Applying…' : 'Apply plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function pickToParticipation(
  pick: AutofillPlan['picks'][number],
  existing: Participation | undefined,
): Participation {
  return {
    id: existing?.id ?? -pick.candidate.player.id,
    eventId: existing?.eventId ?? 0,
    playerId: pick.candidate.player.id,
    appliedA: existing?.appliedA ?? false,
    appliedB: existing?.appliedB ?? false,
    assignedSlot: pick.slot,
    assignedRole: pick.role,
    assignedPosition: pick.position,
    flex: existing?.flex ?? false,
    attendanceStatus: existing?.attendanceStatus ?? null,
    notes: existing?.notes ?? null,
    recordedAt: existing?.recordedAt ?? '',
    attendanceRecordedAt: existing?.attendanceRecordedAt ?? null,
    swapContactMade: existing?.swapContactMade ?? false,
    swapAim15Min: existing?.swapAim15Min ?? false,
    player: { ...(existing?.player ?? pick.candidate.player), latestSnapshot: pick.candidate.latestSnapshot },
    metrics: existing?.metrics ?? null,
  };
}

function PlanCopyDmButton({
  eventType,
  slot,
  startsAtServer,
  plan,
  participations,
}: {
  eventType: EventType;
  slot: SlotKey;
  startsAtServer: string | undefined;
  plan: AutofillPlan;
  participations: Participation[];
}) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(() => {
    const existingBySlot = participations.filter((p) => p.assignedSlot === slot);
    const existingByPlayer = new Map(existingBySlot.map((p) => [p.playerId, p]));
    const merged = new Map<number, Participation>();
    for (const p of existingBySlot) merged.set(p.playerId, p);
    for (const pick of plan.picks.filter((p) => p.slot === slot)) {
      merged.set(
        pick.candidate.player.id,
        pickToParticipation(pick, existingByPlayer.get(pick.candidate.player.id)),
      );
    }
    const rows = [...merged.values()];
    const starters = rows.filter((p) => p.assignedRole === 'starter');
    const subs = rows.filter((p) => p.assignedRole === 'sub');
    return buildSlotDmText({ eventType, slot, startsAtServer, starters, subs });
  }, [eventType, slot, startsAtServer, plan, participations]);

  const hasContent = plan.picks.some((p) => p.slot === slot);
  if (!hasContent) return null;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return (
    <button
      onClick={onCopy}
      className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-[10px] font-medium text-slate-300 normal-case hover:bg-slate-800"
      title={`Copy DM text after applying (${text.length}/${DM_TEXT_MAX})\n\n${text}`}
    >
      <Copy size={11} /> {copied ? 'Copied' : 'Copy DM'}
    </button>
  );
}

